import React, { useEffect, useRef, useState } from 'react';
import {
  lookupSkyDentRecord,
  saveSkyDentRecord,
  requestPhotoUpload,
  uploadPhotoBytes,
  SkyDentEditableData,
  SkyDentPhoto,
  SKYDENT_ALLOWED_PHOTO_TYPES,
} from '../services/skyDentService';
import {
  AIRCRAFT_LOCATION_CATEGORIES,
  findLocationByValue,
  locationValue,
  resolveLocationThumbnail,
} from '../data/aircraftLocations';

/**
 * Props for {@link SkyDentModal}.
 */
interface SkyDentModalProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  /**
   * When true, the record is shown for viewing only — inputs are disabled and
   * Save is hidden. Mirrors Aether's edit-permission flag (the event modal's
   * isReadOnly), so only users who can edit the event can edit its SkyDent data.
   */
  readOnly?: boolean;
  /**
   * When true, the Damage Location is a required field (the event is Completed
   * with a Damage IROP) and is highlighted like other required fields while blank.
   */
  requireDamageLocation?: boolean;
}

const MAX_TEXT = 5000;
const MAX_DAMAGE_LOCATION = 200;
const MAX_PHOTOS = 50;
const MAX_FILENAME = 255;
// "<code> - <label>", e.g. "821 - Aft Lower Cargo Compartment - Deck".
const DAMAGE_LOCATION_PATTERN = /^\S+\s-\s.+$/;

/** Client-side mirror of SkyDent's validation (SkyDent re-validates server-side). */
function validate(data: SkyDentEditableData): string[] {
  const errors: string[] = [];
  if (data.root_cause.length > MAX_TEXT)
    errors.push(`Root Cause must be <= ${MAX_TEXT} characters.`);
  if (data.notes.length > MAX_TEXT) errors.push(`Notes must be <= ${MAX_TEXT} characters.`);
  if (data.damage_location) {
    if (data.damage_location.length > MAX_DAMAGE_LOCATION)
      errors.push(`Damage Location must be <= ${MAX_DAMAGE_LOCATION} characters.`);
    else if (!DAMAGE_LOCATION_PATTERN.test(data.damage_location))
      errors.push('Damage Location must be formatted "<code> - <label>".');
  }
  return errors;
}

/**
 * In-Aether SkyDent edit window. Loads the correlating SkyDent damage record and
 * edits the four SkyDent-owned fields (root cause, notes, damage location,
 * photos). Saving forwards to SkyDent's API so SkyDent-only users see the
 * change; everything else on the record is read-only.
 */
const SkyDentModal: React.FC<SkyDentModalProps> = ({
  eventId,
  isOpen,
  onClose,
  onSaved,
  readOnly = false,
  requireDamageLocation = false,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [rootCause, setRootCause] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [damageLocation, setDamageLocation] = useState<string>('');
  const [locationCategoryCode, setLocationCategoryCode] = useState<string>('');
  const [thumbError, setThumbError] = useState<boolean>(false);
  const [photos, setPhotos] = useState<SkyDentPhoto[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    lookupSkyDentRecord(eventId).then((view) => {
      if (cancelled) return;
      if (!view.exists) {
        setError('No correlating SkyDent record was found for this event.');
      } else {
        setRootCause(view.editable.root_cause);
        setNotes(view.editable.notes);
        setDamageLocation(view.editable.damage_location);
        const match = findLocationByValue(view.editable.damage_location);
        setLocationCategoryCode(match ? match.category.code : '');
        setThumbError(false);
        setPhotos(view.editable.photos);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, eventId]);

  if (!isOpen) return null;

  const handleSave = async (): Promise<void> => {
    const data: SkyDentEditableData = {
      root_cause: rootCause,
      notes,
      damage_location: damageLocation,
      photos,
    };
    const clientErrors = validate(data);
    if (clientErrors.length > 0) {
      setError(clientErrors.join(' '));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await saveSkyDentRecord(eventId, data);
      if (result.ok) {
        if (onSaved) onSaved();
        onClose();
      } else {
        setError(
          (result.errors && result.errors.join(' ')) ||
            result.message ||
            'Failed to save to SkyDent.',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const removePhoto = (index: number): void => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (code: string): void => {
    setLocationCategoryCode(code);
    setDamageLocation('');
    setThumbError(false);
  };

  const handleLocationChange = (value: string): void => {
    setDamageLocation(value);
    setThumbError(false);
  };

  const selectedCategory =
    AIRCRAFT_LOCATION_CATEGORIES.find((category) => category.code === locationCategoryCode) || null;
  const selectedMatch = findLocationByValue(damageLocation);
  // Highlight the Damage Location selects like other required fields when the
  // event is Completed with a Damage IROP and the location is still blank.
  const damageLocationRequiredMissing = requireDamageLocation && damageLocation.trim().length === 0;
  const locationSelectClass = `field-input skydent-location-select${damageLocationRequiredMissing ? ' field-required-missing' : ''}`;

  const handleAddPhoto = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files && event.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      setError(`No more than ${MAX_PHOTOS} photos are allowed.`);
      return;
    }
    if (!SKYDENT_ALLOWED_PHOTO_TYPES.includes(file.type)) {
      setError('Unsupported file type. Allowed: JPEG, PNG, GIF, WEBP, BMP, HEIC, PDF.');
      return;
    }
    if (file.name.length > MAX_FILENAME) {
      setError(`Filename must be <= ${MAX_FILENAME} characters.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const handle = await requestPhotoUpload(eventId, file.name, file.type);
      if (!handle) {
        setError('Could not start the photo upload.');
        return;
      }
      const uploaded = await uploadPhotoBytes(handle.uploadUrl, file, file.type);
      if (!uploaded) {
        setError('Photo upload failed.');
        return;
      }
      const photo: SkyDentPhoto = {
        id: handle.photoId,
        filename: handle.filename,
        contentType: file.type,
        key: handle.key,
        uploaded_at: new Date().toISOString(),
      };
      setPhotos((prev) => [...prev, photo]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="barrier-popup-overlay">
      <div className="barrier-popup skydent-modal" onClick={(e) => e.stopPropagation()}>
        <div className="barrier-popup-header">
          <h3>SkyDent — {readOnly ? 'View' : 'Edit'} Damage Record</h3>
          <button className="modal-close" type="button" onClick={onClose}>
            &times;
          </button>
        </div>
        <div className="barrier-popup-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {loading && <div className="defect-empty">Loading SkyDent record…</div>}
          {!loading && error && (
            <div className="field-inline-error" role="alert">
              {error}
            </div>
          )}
          {!loading && (
            <>
              <div className="field-input-block skydent-field">
                <span className="field-input-label">Root Cause</span>
                <textarea
                  className="field-textarea"
                  rows={3}
                  maxLength={MAX_TEXT}
                  value={rootCause}
                  onChange={(e) => setRootCause(e.target.value)}
                  placeholder="Root cause of the damage"
                  readOnly={readOnly}
                />
              </div>
              <div className="field-input-block skydent-field">
                <span className="field-input-label">Notes</span>
                <textarea
                  className="field-textarea"
                  rows={3}
                  maxLength={MAX_TEXT}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Notes"
                  readOnly={readOnly}
                />
              </div>
              <div className="field-input-block skydent-field">
                <span className="field-input-label">Damage Location</span>
                <div className="skydent-location-picker">
                  <select
                    className={locationSelectClass}
                    value={locationCategoryCode}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    disabled={readOnly}
                    aria-label="Damage location area"
                  >
                    <option value="">Select area…</option>
                    {AIRCRAFT_LOCATION_CATEGORIES.map((category) => (
                      <option key={category.code} value={category.code}>
                        {category.code} - {category.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className={locationSelectClass}
                    value={selectedMatch ? damageLocation : ''}
                    onChange={(e) => handleLocationChange(e.target.value)}
                    disabled={!selectedCategory || readOnly}
                    aria-label="Damage location"
                  >
                    <option value="">
                      {selectedCategory ? 'Select location…' : 'Select an area first'}
                    </option>
                    {selectedCategory?.locations.map((location) => (
                      <option key={locationValue(location)} value={locationValue(location)}>
                        {location.code} - {location.label}
                      </option>
                    ))}
                  </select>
                </div>
                {damageLocation && !selectedMatch && (
                  <span className="field-hint">
                    Current value (not in catalog): {damageLocation}
                  </span>
                )}
                {selectedMatch && !thumbError && (
                  <img
                    className="skydent-location-thumb"
                    src={resolveLocationThumbnail(selectedMatch.location)}
                    alt={`${selectedMatch.location.label} location diagram`}
                    onError={() => setThumbError(true)}
                  />
                )}
              </div>
              <div className="field-input-block skydent-field">
                <span className="field-input-label">Photos ({photos.length})</span>
                {photos.length === 0 ? (
                  <span className="defect-empty">No photos attached</span>
                ) : (
                  photos.map((photo, index) => (
                    <div key={`${photo.filename}-${index}`} className="skydent-photo-row">
                      <span className="skydent-photo-name">{photo.filename}</span>
                      <span className="skydent-photo-type">{photo.contentType}</span>
                      {!readOnly && (
                        <button
                          type="button"
                          className="barrier-delete-btn"
                          onClick={() => removePhoto(index)}
                          aria-label="Remove photo"
                          title="Remove photo"
                        >
                          &#128465;
                        </button>
                      )}
                    </div>
                  ))
                )}
                {!readOnly && (
                  <div className="skydent-photo-add">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={SKYDENT_ALLOWED_PHOTO_TYPES.join(',')}
                      onChange={handleAddPhoto}
                      disabled={uploading || photos.length >= MAX_PHOTOS}
                    />
                    {uploading && <span className="defect-empty">Uploading…</span>}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="barrier-popup-footer">
          <button className="modal-btn modal-btn-cancel" type="button" onClick={onClose}>
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              className="modal-btn modal-btn-save"
              type="button"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving…' : 'Save to SkyDent'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkyDentModal;
